import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { formatDZ } from '../../../utils/currency';

const ComparisonTable = ({ products, onRemoveProduct, onClose, onAddToCart }) => {
  if (!products || products?.length === 0) {
    return null;
  }

  const features = [
    'Price',
    'Rating',
    'Brand',
    'Category',
    'Availability',
    'Key Features',
    'Warranty',
    'Shipping'
  ];

  const getFeatureValue = (product, feature) => {
    switch (feature) {
      case 'Price':
        return (
          <div className="space-y-1">
            <div className="text-lg font-bold text-foreground">{formatDZ(product?.price)}</div>
            {product?.originalPrice && (
              <div className="text-sm text-muted-foreground line-through">
                {formatDZ(product?.originalPrice)}
              </div>
            )}
          </div>
        );
      case 'Rating':
        return (
          <div className="flex items-center space-x-2">
            <div className="flex items-center">
              {Array.from({ length: 5 })?.map((_, i) => (
                <Icon
                  key={i}
                  name="Star"
                  size={14}
                  className={i < Math.floor(product?.rating) ? "text-warning fill-current" : "text-muted-foreground"}
                />
              ))}
            </div>
            <span className="text-sm text-muted-foreground">({product?.reviewCount})</span>
          </div>
        );
      case 'Brand':
        return <span className="text-primary font-medium">{product?.brand}</span>;
      case 'Category':
        return <span className="text-muted-foreground">{product?.category}</span>;
      case 'Availability':
        const getStatusColor = (status) => {
          switch (status) {
            case 'in-stock': return 'text-success';
            case 'low-stock': return 'text-warning';
            case 'pre-order': return 'text-primary';
            case 'out-of-stock': return 'text-error';
            default: return 'text-muted-foreground';
          }
        };
        return (
          <span className={`font-medium ${getStatusColor(product?.availability)}`}>
            {product?.availability?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
          </span>
        );
      case 'Key Features':
        return (
          <div className="space-y-1">
            {product?.keyFeatures?.slice(0, 3)?.map((feature, index) => (
              <div key={index} className="text-sm text-muted-foreground">
                • {feature}
              </div>
            ))}
          </div>
        );
      case 'Warranty':
        return <span className="text-sm text-muted-foreground">{product?.warranty || '1 Year'}</span>;
      case 'Shipping':
        return (
          <div className="flex items-center space-x-1">
            <Icon name="Truck" size={14} className={product?.freeShipping ? "text-success" : "text-muted-foreground"} />
            <span className={`text-sm ${product?.freeShipping ? "text-success" : "text-muted-foreground"}`}>
              {product?.freeShipping ? 'Free' : 'Paid'}
            </span>
          </div>
        );
      default:
        return <span className="text-muted-foreground">-</span>;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.9 }}
          className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-3">
              <Icon name="GitCompare" size={24} className="text-primary" />
              <h2 className="text-xl font-semibold text-foreground">Product Comparison</h2>
              <span className="text-sm text-muted-foreground">({products?.length} products)</span>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Comparison Table */}
          <div className="overflow-auto max-h-[calc(90vh-120px)]">
            <table className="w-full">
              <thead className="bg-muted/20 sticky top-0 z-10">
                <tr>
                  <th className="text-left p-4 font-medium text-foreground min-w-[150px]">
                    Features
                  </th>
                  {products?.map((product) => (
                    <th key={product?.id} className="text-center p-4 min-w-[250px]">
                      <div className="space-y-3">
                        {/* Product Image */}
                        <div className="relative">
                          <Image
                            src={product?.image}
                            alt={product?.name}
                            className="w-20 h-20 object-cover rounded-lg mx-auto"
                          />
                          <Button
                            variant="destructive"
                            size="icon"
                            onClick={() => onRemoveProduct(product?.id)}
                            className="absolute -top-2 -right-2 w-6 h-6"
                          >
                            <Icon name="X" size={12} />
                          </Button>
                        </div>
                        
                        {/* Product Name */}
                        <div className="text-sm font-medium text-foreground line-clamp-2">
                          {product?.name}
                        </div>
                        
                        {/* Add to Cart Button */}
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => onAddToCart(product?.id)}
                          disabled={product?.availability === 'out-of-stock'}
                          iconName="ShoppingCart"
                          iconPosition="left"
                          className="w-full"
                        >
                          {product?.availability === 'out-of-stock' ? 'Out of Stock' : 'Add to Cart'}
                        </Button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features?.map((feature, index) => (
                  <tr key={feature} className={index % 2 === 0 ? 'bg-muted/10' : 'bg-transparent'}>
                    <td className="p-4 font-medium text-foreground border-r border-border">
                      {feature}
                    </td>
                    {products?.map((product) => (
                      <td key={product?.id} className="p-4 text-center border-r border-border last:border-r-0">
                        {getFeatureValue(product, feature)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-border bg-muted/10">
            <div className="text-sm text-muted-foreground">
              Compare up to 4 products side by side
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={onClose}>
                Close Comparison
              </Button>
              <Button variant="default" iconName="Share2" iconPosition="left">
                Share Comparison
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ComparisonTable;